import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarCheck, CheckCircle2, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ARTICLES, CATEGORY_IMAGES } from "@/data/articles";
import SEO from "@/components/SEO";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="bg-cream min-h-[60vh] flex items-center justify-center px-5">
        <div className="text-center">
          <h1 className="font-serif text-3xl font-semibold text-navy">Article not found</h1>
          <Link to="/learn" className="mt-4 inline-flex items-center gap-2 font-bold text-navy hover:text-gold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Legacy Academy
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream">
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/learn/${article.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          author: { "@type": "Organization", name: "Legacy Wave Consulting" },
          publisher: { "@type": "Organization", name: "Legacy Wave Consulting" },
        }}
      />
      <div className="bg-navy text-cream hero-grain relative overflow-hidden">
        <div className="absolute inset-0 gold-glow" />
        <div className="relative max-w-3xl mx-auto px-5 py-14 md:py-20">
          <Reveal y={14}>
            <Link to="/learn" className="inline-flex items-center gap-2 text-sm font-bold text-cream/70 hover:text-gold transition-colors mb-6" data-testid="article-back-link">
              <ArrowLeft className="w-4 h-4" /> Legacy Academy™
            </Link>
            <span className="block text-xs font-bold uppercase tracking-[0.28em] text-gold">{article.category}</span>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-semibold leading-tight">{article.title}</h1>
            <p className="mt-4 flex items-center gap-2 text-sm text-cream/70 font-semibold">
              <Clock className="w-4 h-4 text-gold" /> {article.minutes}-minute read
            </p>
          </Reveal>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-12">
        {CATEGORY_IMAGES[article.category] && (
          <Reveal y={14}>
            <div className="rounded-3xl overflow-hidden border border-sand shadow-lg aspect-[16/8] mb-8">
              <img src={CATEGORY_IMAGES[article.category]} alt={`${article.category} — a family together`} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        )}
        <div className="bg-gold-light border border-sand rounded-3xl p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Key Takeaways</p>
          <ul className="space-y-3">
            {article.takeaways.map((t) => (
              <li key={t} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span className="text-navy font-medium leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 space-y-10">
          {article.sections.map((s, i) => (
            <Reveal key={s.heading} delay={0.05}>
              <section>
                <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-navy leading-snug">
                  <span className="text-gold/50 mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                {s.body.map((p) => (
                  <p key={p.slice(0, 80)} className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed">{p}</p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 bg-navy text-cream rounded-3xl p-8 sm:p-10 hero-grain relative overflow-hidden">
            <div className="absolute inset-0 gold-glow" />
            <div className="relative">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold leading-snug">
                Have questions about your situation?
              </h2>
              <p className="mt-3 text-cream/75 leading-relaxed">
                A complimentary Legacy Review is a relaxed, educational conversation — no pressure, no obligation.
              </p>
              <Link
                to="/book"
                data-testid="article-book-button"
                className="mt-6 inline-flex items-center gap-2 min-h-[56px] rounded-full bg-gold hover:bg-gold-hover text-navy font-extrabold text-sm tracking-widest px-8 transition-all hover:-translate-y-0.5"
              >
                <CalendarCheck className="w-5 h-5" /> SCHEDULE A LEGACY REVIEW
              </Link>
            </div>
          </div>
        </Reveal>

        <p className="mt-8 text-xs text-slate-500 leading-relaxed">
          This article is educational only and is not legal, tax, investment, or financial advice. Estate-planning
          documents and legal advice should be provided by a qualified attorney.
        </p>

        <div className="mt-10 pt-8 border-t border-sand">
          <Link to="/learn" className="inline-flex items-center gap-2 font-bold text-navy hover:text-gold transition-colors">
            <ArrowLeft className="w-4 h-4" /> More from Legacy Academy <ArrowRight className="w-4 h-4 hidden" />
          </Link>
        </div>
      </div>
    </div>
  );
}
